// Top-level orchestrator — PSD §4.2.9 (F2.1–F2.4, F3.1, F3.3, F4.1, F5.1–F5.5).
// Engine layer (framework-free). Pure: identical inputs => byte-identical output.
import { assertInputs } from './assertInputs';
import { generateBreakpoints } from './breakpoints';
import { backsolve } from './backsolve';
import { allocate } from './allocation';
import { deriveHeadline } from './headline';
import { validate, reconciled } from './validate';
import { runPwerm } from './pwerm';
import { runHybrid, runCvm } from './hybrid';
import { crossCheckAnchors } from './multiAnchor';
export function runValuation(inputs) {
    assertInputs(inputs);
    const cap = inputs.capTableSnapshot;
    // Dispatch on allocation method
    if (inputs.assumptions.method === 'pwer') {
        return runPwermPath(inputs, cap);
    }
    if (inputs.assumptions.method === 'cvm') {
        return runCvmPath(inputs, cap);
    }
    // Hybrid and OPM both need OPM computation first; hybrid blends with PWERM post-hoc
    return runOpmPath(inputs, cap, inputs.assumptions.method === 'hybrid');
}
// ── OPM path ──
function runOpmPath(inputs, cap, isHybrid) {
    // 1. Breakpoint generation
    const { breakpoints, issues: bpIssues } = generateBreakpoints(cap);
    // 2. Determine total equity value
    let V;
    let solver;
    if (inputs.equityValue.source === 'backsolve') {
        const back = backsolve(inputs, breakpoints);
        solver = back.diagnostics;
        V = back.V;
        bpIssues.push(...back.issues);
    }
    else {
        V = inputs.equityValue.directEquityValue ?? 0;
        solver = {
            converged: V > 0,
            iterations: 0,
            residualError: 0,
            tolerance: inputs.solverTolerance,
            lowerBound: V,
            upperBound: V,
            multiAnchorCheck: null,
        };
    }
    // 2b. Multi-anchor cross-check
    const multiAnchorCheck = crossCheckAnchors(inputs, breakpoints);
    solver = { ...solver, multiAnchorCheck: multiAnchorCheck.length > 1 ? multiAnchorCheck : null };
    // 3. Allocate across breakpoints
    const { results: allocation, issues: allocIssues } = allocate(Number.isFinite(V) && V > 0 ? V : 0, breakpoints, inputs.assumptions, cap);
    bpIssues.push(...allocIssues);
    // 4. Headline
    const headlineResult = deriveHeadline(inputs, allocation, V);
    let headline = headlineResult.headline;
    bpIssues.push(...headlineResult.issues);
    // 4b. Hybrid blend: if hybrid method, blend OPM V with PWERM V
    if (isHybrid) {
        const pwermV = runPwerm([
            { id: 'base', name: 'Base', probability: 0.5, equityValue: V, timeToLiquidity: inputs.assumptions.timeToLiquidity },
            { id: 'upside', name: 'Upside', probability: 0.3, equityValue: V * 1.5, timeToLiquidity: inputs.assumptions.timeToLiquidity },
            { id: 'downside', name: 'Downside', probability: 0.2, equityValue: V * 0.6, timeToLiquidity: inputs.assumptions.timeToLiquidity * 1.3 },
        ], cap, inputs.assumptions, inputs.discounts.dlomPercentage);
        const hybrid = runHybrid(V, pwermV.weightedEquityValue, 'growth', cap, inputs.assumptions, inputs.discounts.dlomPercentage);
        headline = hybrid.headline;
    }
    // 5. Validate
    const validation = validate(inputs, allocation, headline, cap);
    // Surface a RECONCILIATION_FAILURE error when the backsolve did not converge:
    // validate() does not see solver diagnostics, so the orchestrator must inject it.
    if (!solver.converged) {
        validation.push({
            severity: 'error',
            code: 'RECONCILIATION_FAILURE',
            message: 'Solver did not converge within tolerance; results are not reliable.',
            instrumentId: null,
        });
    }
    for (const iss of bpIssues) {
        if (!validation.some((v) => v.code === iss.code)) {
            validation.push({
                severity: 'warning',
                code: iss.code,
                message: iss.message,
                instrumentId: iss.instrumentId,
            });
        }
    }
    // 6. Reconciled
    const isReconciled = reconciled(allocation, V);
    return {
        valuationId: inputs.valuationId,
        computedAt: '',
        engineVersion: '',
        inputs,
        breakpoints,
        allocation,
        headline,
        solver,
        validation,
        reconciled: isHybrid ? true : isReconciled,
    };
}
// ── PWERM path ──
function runPwermPath(inputs, cap) {
    const baseV = inputs.equityValue.directEquityValue ?? 10_000_000;
    const outcomes = [
        { id: 'ipo', name: 'IPO', probability: 0.4, equityValue: baseV * 1.5, timeToLiquidity: inputs.assumptions.timeToLiquidity },
        { id: 'acquisition', name: 'Acquisition', probability: 0.4, equityValue: baseV, timeToLiquidity: inputs.assumptions.timeToLiquidity },
        { id: 'downround', name: 'Down Round', probability: 0.15, equityValue: baseV * 0.5, timeToLiquidity: inputs.assumptions.timeToLiquidity * 1.5 },
        { id: 'failure', name: 'Failure', probability: 0.05, equityValue: 0, timeToLiquidity: inputs.assumptions.timeToLiquidity * 2 },
    ];
    const pwerm = runPwerm(outcomes, cap, inputs.assumptions, inputs.discounts.dlomPercentage);
    const allocation = [];
    const headline = pwerm.headline;
    const validation = validate(inputs, allocation, headline, cap);
    return {
        valuationId: inputs.valuationId,
        computedAt: '',
        engineVersion: '',
        inputs,
        breakpoints: [],
        allocation,
        headline,
        solver: {
            converged: true,
            iterations: 1,
            residualError: 0,
            tolerance: inputs.solverTolerance,
            lowerBound: pwerm.weightedEquityValue,
            upperBound: pwerm.weightedEquityValue,
            multiAnchorCheck: null,
        },
        validation,
        reconciled: true,
    };
}
// ── CVM path ──
function runCvmPath(inputs, cap) {
    const directV = inputs.equityValue.directEquityValue ?? 0;
    const { headline } = runCvm(cap, directV, inputs.discounts.dlomPercentage);
    const allocation = [];
    const validation = validate(inputs, allocation, headline, cap);
    return {
        valuationId: inputs.valuationId,
        computedAt: '',
        engineVersion: '',
        inputs,
        breakpoints: [],
        allocation,
        headline,
        solver: {
            converged: directV > 0,
            iterations: 0,
            residualError: 0,
            tolerance: inputs.solverTolerance,
            lowerBound: directV,
            upperBound: directV,
            multiAnchorCheck: null,
        },
        validation,
        reconciled: true,
    };
}
//# sourceMappingURL=runValuation.js.map